<template>
  <div
    ref="messagesContainer"
    class="wa__messages"
    :style="{ backgroundImage: `url(${chatBg})` }"
  >
    <template v-for="item in groupedMessages" :key="groupedMessageKey(item)">
      <div v-if="item.type === 'date-separator'" class="wa__date-separator">
        <span>{{ item.label }}</span>
      </div>

      <div
        v-else
        class="wa__msg-row"
        :class="
          item.message.role === 'assistant'
            ? 'wa__msg-row--user'
            : 'wa__msg-row--agent'
        "
      >
        <!-- Location -->
        <div
          v-if="getMessageLocation(item.message)"
          class="wa__bubble wa__bubble--agent wa__bubble--location"
        >
          <div class="wa__location-map-wrap">
            <img
              class="wa__location-map"
              :src="
                staticMapUrl(
                  getMessageLocation(item.message).latitude,
                  getMessageLocation(item.message).longitude,
                )
              "
              alt="Mapa"
            />
            <span class="wa__location-pin">📍</span>
          </div>
          <div class="wa__location-coords">
            {{ getMessageLocation(item.message).latitude }},
            {{ getMessageLocation(item.message).longitude }}
          </div>
          <button
            type="button"
            class="wa__coverage-btn wa__copy-btn"
            @click="
              copyCoordinates(
                getMessageLocation(item.message).latitude,
                getMessageLocation(item.message).longitude,
              )
            "
          >
            {{
              copiedCoords ===
              `${getMessageLocation(item.message).latitude},${getMessageLocation(item.message).longitude}`
                ? "Copiado"
                : "Copiar coordenadas"
            }}
          </button>
          <div class="wa__location-footer">
            <span v-if="item.message.timestamp" class="wa__bubble-time">
              {{ formatTime(item.message.timestamp) }}
            </span>
          </div>
        </div>

        <!-- Audio / voice note -->
        <div
          v-else-if="item.message.isVoiceNote || item.message.type === 'audio'"
          class="wa__bubble"
          :class="
            item.message.role === 'assistant'
              ? 'wa__bubble--user'
              : 'wa__bubble--agent'
          "
        >
          <div class="wa__voice-note">
            <div v-if="item.message.audioUrl" class="wa__voice-player">
              <audio
                :src="item.message.audioUrl"
                controls
                preload="none"
                class="wa__audio"
                @error="handleAudioError(item.message)"
              ></audio>
            </div>

            <div
              v-else-if="!item.message.audioExpired"
              class="wa__voice-loading"
            >
              <i class="fas fa-microphone"></i>
              <span>Nota de voz</span>
            </div>

            <div v-else class="wa__voice-expired">
              <i class="fas fa-microphone-slash"></i>
              <span>Nota de voz caducada</span>
            </div>

            <div class="wa__voice-transcription">
              <i
                class="fas fa-quote-left"
                style="font-size: 10px; opacity: 0.5"
              ></i>
              <span>{{
                item.message.transcription ||
                extractText(item.message.content)
              }}</span>
            </div>
          </div>

          <span v-if="item.message.timestamp" class="wa__bubble-time">
            {{ formatTime(item.message.timestamp) }}
          </span>
        </div>

        <!-- Text -->
        <div v-else class="wa__bubble" :class="bubbleClass(item.message)">
          <span class="wa__bubble-text">{{
            extractText(item.message.content)
          }}</span>
          <span v-if="item.message.timestamp" class="wa__bubble-time">
            {{ formatTime(item.message.timestamp) }}
          </span>
          <span v-if="item.message.sentByAdvisor" class="wa__advisor-label">
            asesor
          </span>
        </div>
      </div>
    </template>

    <div v-if="typing" class="wa__msg-row wa__msg-row--agent">
      <div class="wa__bubble wa__bubble--agent wa__bubble--typing">
        <span class="wa__typing-dots">
          <span></span><span></span><span></span>
        </span>
        <span class="wa__typing-label">Escribiendo…</span>
      </div>
    </div>
  </div>
</template>

<script>
const ECUADOR_TZ = "America/Guayaquil";

export default {
  name: "WaMessages",
  props: {
    groupedMessages: {
      type: Array,
      required: true,
    },
    typing: {
      type: Boolean,
      default: false,
    },
    chatBg: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      copiedCoords: null,
    };
  },
  methods: {
    bubbleClass(message) {
      if (message.role === "assistant") {
        if (message.sentByAdvisor) {
          return "wa__bubble--user wa__bubble--advisor-sent";
        }
        return "wa__bubble--user";
      }
      return "wa__bubble--agent";
    },
    scrollToBottom() {
      const el = this.$refs.messagesContainer;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },
    extractText(content) {
      if (typeof content === "string") return content;
      if (Array.isArray(content)) {
        return content
          .filter((b) => b.type === "text")
          .map((b) => b.text)
          .join("\n");
      }
      return "";
    },
    toMs(timestamp) {
      if (!timestamp) return 0;
      if (typeof timestamp.toMillis === "function") return timestamp.toMillis();
      if (timestamp.seconds) return timestamp.seconds * 1000;
      return new Date(timestamp).getTime() || 0;
    },
    formatTime(timestamp) {
      if (!timestamp) return "";
      const ms = this.toMs(timestamp);
      return new Date(ms).toLocaleTimeString("es-EC", {
        timeZone: ECUADOR_TZ,
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    staticMapUrl(lat, lng) {
      const zoom = 15;
      const n = Math.pow(2, zoom);
      const x = Math.floor(((lng + 180) / 360) * n);
      const latRad = (lat * Math.PI) / 180;
      const y = Math.floor(
        ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) *
          n,
      );
      return `https://tile.openstreetmap.org/${zoom}/${x}/${y}.png`;
    },
    handleAudioError(message) {
      message.audioExpired = true;
    },
    async copyCoordinates(lat, lng) {
      const text = `${lat},${lng}`;
      try {
        await navigator.clipboard.writeText(text);
        this.copiedCoords = text;
        setTimeout(() => {
          this.copiedCoords = null;
        }, 2000);
      } catch (err) {
        const el = document.createElement("textarea");
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        this.copiedCoords = text;
        setTimeout(() => {
          this.copiedCoords = null;
        }, 2000);
      }
    },
    groupedMessageKey(item) {
      if (!item) return "unknown";
      if (item.type === "date-separator") return `date:${item.label || ""}`;
      const m = item.message || {};
      if (m.isVoiceNote || m.type === "audio") {
        return `voice:${m.sourceMessageId || m.timestamp || ""}`;
      }
      const contentKey = this.extractText(m.content || "").slice(0, 40);
      return `msg:${m.timestamp || ""}:${m.role || ""}:${contentKey}`;
    },
    getMessageLocation(message) {
      if (message?.type === "location" && message.location) {
        return {
          latitude: Number(message.location.latitude),
          longitude: Number(message.location.longitude),
        };
      }

      const text =
        typeof message?.content === "string"
          ? message.content
          : this.extractText(message?.content);

      if (!text) return null;

      const match = text.match(
        /latitud\s+([-\d.]+).*longitud\s+([-\d.]+)/i,
      );
      if (match) {
        return {
          latitude: parseFloat(match[1]),
          longitude: parseFloat(match[2]),
        };
      }

      return null;
    },
  },
};
</script>

<style lang="scss">
.wa__date-separator {
  display: flex;
  justify-content: center;
  margin: 12px 0;

  span {
    background: rgba(255, 255, 255, 0.85);
    color: #667781;
    font-size: 12px;
    font-weight: 500;
    padding: 4px 12px;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  }
}

.wa__location-coords {
  font-size: 11px;
  color: #667781;
  margin: 6px 0 4px;
  word-break: break-all;
}
</style>
